import { TCardFacet, TFacet } from '@/types/collection'
import { getPreservedParamsForCollection } from '../../../constants'
import { FilterValues } from '../../../types/filter-store-v2.types'
import { urlBuilder } from '../../../utils/url-builder.service'

/**
 * Build URL for a filter option (checkbox or radio)
 * @param params - Parameters for building the URL
 * @returns The URL string for the filter option
 */
export function buildFilterUrl(params: {
  originalSlug: string
  currentFilters: FilterValues
  facetKey: string
  facetValue: string
  isChecked: boolean // For checkbox: true if should be checked, false if should be unchecked
  isMultiple: boolean // Whether the facet allows multiple values
  isConcatSlug: boolean // Whether this facet uses concat slug
  facets?: TFacet[]
  cardFacets?: TCardFacet[]
  currentPathname?: string
}): string {
  const {
    originalSlug,
    currentFilters,
    facetKey,
    facetValue,
    isChecked,
    isMultiple,
    isConcatSlug,
    facets,
    cardFacets,
    currentPathname,
  } = params

  // Create new filters object based on current state
  const newFilters: FilterValues = { ...currentFilters }

  if (isMultiple) {
    // Checkbox: handle multiple values
    const currentValues = Array.isArray(newFilters[facetKey])
      ? (newFilters[facetKey] as string[])
      : newFilters[facetKey]
        ? [String(newFilters[facetKey])]
        : []

    if (isChecked) {
      // Add value if not already present
      if (!currentValues.includes(facetValue)) {
        newFilters[facetKey] = [...currentValues, facetValue]
      }
    } else {
      // Remove value
      const filtered = currentValues.filter((v) => v !== facetValue)
      if (filtered.length === 0) {
        delete newFilters[facetKey]
      } else {
        newFilters[facetKey] = filtered
      }
    }
  } else {
    // Radio: single value
    if (isChecked) {
      newFilters[facetKey] = facetValue
    } else {
      // Radio doesn't support uncheck, but handle edge case
      delete newFilters[facetKey]
    }
  }

  // Don't manually determine concatValue - let buildCollectionUrl handle it automatically
  // buildCollectionUrl will find the first concat facet with values and use the first value
  // This ensures consistency and prevents conflicts when multiple concat facets exist

  // Get preserved params for this collection
  const preservedParams = getPreservedParamsForCollection(originalSlug)

  // Build URL using urlBuilder (concatValue will be determined automatically from filters)
  const url = urlBuilder.buildCollectionUrl({
    originalSlug,
    filters: newFilters,
    concatValue: null, // Let buildCollectionUrl determine concat value from filters
    currentPathname:
      currentPathname ||
      (typeof window !== 'undefined' ? window.location.pathname : ''),
    facets,
    cardFacets,
    preserveParams: preservedParams.length > 0 ? preservedParams : undefined,
  })

  return url
}
